/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState } from "react";
import {
  addAskingTeamMember,
  editAskingTeamMember,
} from "services/doubtfulService";
import { DoubtfulCase, TeamMemberAsking } from "types";
import { BASE_URL, LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  action: "Add" | "Edit";
  instruction: DoubtfulCase;
  askingTeam?: TeamMemberAsking;
  onClose: (refetch: boolean) => void;
};

const AskingTeamMemberForm: FC<Props> = ({
  instruction,
  askingTeam,
  action,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [prosecutors, setProsecutors] = useState<
    {
      user: {
        id: string;
        first_name: string;
        last_name: string;
      };
    }[]
  >([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [isLeader, setIsLeader] = useState<boolean>(false);

  const fetchData = (adminId: string | number, orgId: string | number) => {
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${adminId}&org_id=${orgId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        setProsecutors(
          data?.filter(
            (d: any) =>
              d?.roles?.includes("Prosecutor") ||
              d?.roles?.includes("ProsecutionCaseProsecutor"),
          ),
        );
      })
      .catch((error) => {
        console.error("Error fetching prosecutors:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData) {
      setLoading(true);
      fetchData(loginData?.admin_unit, loginData?.org_unit);
    }
  }, [loginData]);

  useEffect(() => {
    if (askingTeam && prosecutors) {
      setSelectedId(askingTeam?.admin_unit_id ?? null);
      setSelectedName(askingTeam?.name);
      setIsLeader(askingTeam?.is_team_leader);
    }
  }, [askingTeam, prosecutors]);

  const validateForm = () => {
    if (!selectedId) return t("prosecutor_is_required");
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    // Submit the FormData
    const submitAction =
      action === "Edit" ? editAskingTeamMember : addAskingTeamMember;
    submitAction({
      id: action === "Edit" ? askingTeam?.id : undefined,
      name: selectedName,
      admin_unit_id: selectedId,
      is_team_leader: isLeader,
      instruction: instruction?.id,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? t("successfully_updated")
            : t("successfully_submitted"),
          "success",
        );
        setSelectedId(null);
        setSelectedName(null);
        setIsLeader(false);
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };
  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-2 text-xl font-semibold">
          {`${t(action.toLowerCase())} ${t("asking_team_member")}`}
        </h2>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2 ">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("prosecutor")}
            </label>

            <select
              className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
              name="reception_method"
              value={selectedId || ""}
              onChange={(e) => {
                const selectedProsecutor = prosecutors.find(
                  (p) => p?.user?.id?.toString() === e.target.value,
                );
                if (selectedProsecutor) {
                  setSelectedId(
                    selectedProsecutor.user?.id !== undefined &&
                      selectedProsecutor.user?.id !== null
                      ? String(selectedProsecutor.user?.id)
                      : "",
                  );
                  setSelectedName(
                    selectedProsecutor.user?.first_name +
                      " " +
                      selectedProsecutor.user?.last_name,
                  );
                }
              }}
              required
            >
              <option value={""}>
                {loading ? t("loading") : t("select_prosecutor")}
              </option>
              {Array.isArray(prosecutors) &&
                prosecutors.map((p, index) => (
                  <option key={index} value={p.user?.id || ""}>
                    {p?.user?.first_name + " " + p?.user?.last_name}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-8 py-2 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040] ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                `${t(action.toLowerCase())} ${t("asking_team_member")}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AskingTeamMemberForm;
